import { AddressInfoWrapper } from '../../wrappers'
import { createContext, PropsWithChildren, useCallback, useReducer } from 'react'
import { AddressDetailsData, AddressInfoData } from '../../models'
import { CartItemSlot, useCart } from '../../hooks'

export const addressInfoWrapper = new AddressInfoWrapper().load()

enum AddressActions {
  SAME_SHIPPING,
  SET_BILLING,
  SET_SHIPPING
}

type AddressAction =
  | { type: AddressActions.SAME_SHIPPING, payload: boolean }
  | { type: AddressActions.SET_BILLING, payload: AddressDetailsData }
  | { type: AddressActions.SET_SHIPPING, payload: AddressDetailsData }
const AddressReducer = (state: AddressInfoData, action: AddressAction) => {
  switch(action.type) {
    case AddressActions.SAME_SHIPPING:
      return { ...state, sameAddress: action.payload }
    case AddressActions.SET_BILLING:
      return { ...state, billing: action.payload }
    case AddressActions.SET_SHIPPING:
      return { ...state, shipping: action.payload }
    default:
      return state
  }

}

interface AddressProps {
  data: AddressInfoData
  setSameShipping?: (isSame: boolean) => void
  setBilling?: (details: AddressDetailsData) => void
  setShipping?: (details: AddressDetailsData) => void
  errors?: () => Promise<Error[]>
}

export const AddressContext = createContext<AddressProps>(
  { data: { sameAddress: true }},
)

export const AddressProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const cart = useCart()
  const addressInfo = addressInfoWrapper.data
  const [state, dispatch] =  useReducer(AddressReducer, {...addressInfo, sameAddress: addressInfo.sameAddress ?? true})


  const setSameShipping = useCallback((isSame: boolean) => {
    addressInfoWrapper.setSameAddress(isSame)
    dispatch({ type: AddressActions.SAME_SHIPPING, payload: isSame })
  }, [])

  const setTrialValue = useCallback((details: AddressDetailsData) => {
    const { country } = details
    const trialItem = cart.getItemsBySlot(CartItemSlot.Trial)
    const baseValue = trialItem?.value || 18.25
    const defaultDiscount = 1
    /* eslint-disable */
    const countryDiscounts: Record<string, number> = {
      AE: 0.2,
      AT: 0.7,
      AU: 0.4,
      BE: 0.5,
      BR: 0.1,
      CA: 0.7,
      DE: 0.8,
      CZ: 0.4,
      DK: 0.5,
      EE: 0.4,
      FR: 0.3,
      FI: 0.4,
      GB: 0.7,
      HU: 0.4,
      IE: 0.4,
      CY: 0.2,
      JP: 0.8,
      HR: 0.3,
      PH: 0.1,
      IS: 0.5,
      US: 1.0,
      ES: 0.2,
      BG: 0.1,
      UK: 0.7,
      GR: 0.1,
      PT: 0.3,
      IT: 0.4,
      RO: 0.1,
      LI: 0.5,
      SE: 0.3,
      LT: 0.2,
      LU: 0.4,
      TH: 0.1,
      LV: 0.2,
      TR: 0.1,
      MT: 0.2,
      NL: 0.3,
      NO: 0.7,
      PL: 0.1,
      SI: 0.4,
      SK: 0.2,
    }
    /* eslint-enable */
    const countryDiscount = countryDiscounts[country || ''] ?? defaultDiscount
    const countryValue = parseFloat((baseValue * countryDiscount).toFixed(2))
    if (country && trialItem) {
      cart.addCartItem(CartItemSlot.Trial, { ...trialItem, value: countryValue })
      cart.save()
    }
  }, [cart])

  const setBilling = useCallback((details: AddressDetailsData) => {
    addressInfoWrapper.setBilling(details)
    setTrialValue(details)
    dispatch({ type: AddressActions.SET_BILLING, payload: details })
  }, [setTrialValue])

  const setShipping = useCallback((details: AddressDetailsData) => {
    addressInfoWrapper.setShipping(details)
    setTrialValue(details)
    dispatch({ type: AddressActions.SET_SHIPPING, payload: details })
  }, [setTrialValue])

  const errors = async () => {
    return addressInfoWrapper.errors()
  }

  return (
    <AddressContext.Provider value={{
      data: state,
      errors,
      setSameShipping,
      setBilling,
      setShipping
    }}>
      {children}
    </AddressContext.Provider>
  )
}